import { LE_CODES } from "@/constants/enums";
import { getElectronicFormRouteName } from "@/helpers/electronicFormHelpers";
import { createWorksheetChildRoutes } from "@/views/electronicForm/worksheets/worksheetRoutes";
import { EntityAudit } from "@modernary/mui/components/audit";
import { RouterView, type RouterOptions } from "vue-router";
import { createDocumentChildRoutes } from "../documents/documentRoutes";

// Troubleshoot your routes using this - https://paths.esm.dev
// Reusable HOC for dynamic imports with props
const withProps = (importFn: () => Promise<any>, defaultProps: Record<string, any> = {}) => {
  return {
    component: importFn,
    props: defaultProps,
  };
};

const dustControlPermittingForms: Array<
  keyof Pick<typeof LE_CODES.ElectronicForm, "ef070" | "ef071" | "ef072" | "ef073" | "ef074">
> = ["ef070", "ef071", "ef072", "ef073", "ef074"] as const;

// TemporaryDustControlMonitorWorksheetWork: pilot worksheet routes — remove when reverting temporary work.
const dustControlPermittingWorksheetForms: Array<keyof Pick<typeof LE_CODES.ElectronicForm, "ef099">> = [
  "ef099",
] as const;

const allDustControlPermittingRoutableForms = [
  ...dustControlPermittingForms,
  ...dustControlPermittingWorksheetForms,
] as const;

const nameGenerator = <
  T extends { [key: string]: { new: string; view: string; edit: string; base: string; audit: string } },
>(
  acc: Partial<T>,
  form: (typeof allDustControlPermittingRoutableForms)[number],
) => {
  (acc as any)[`${form}`] = {
    new: getElectronicFormRouteName(LE_CODES.ElectronicForm[form], "New"),
    view: getElectronicFormRouteName(LE_CODES.ElectronicForm[form], "View"),
    edit: getElectronicFormRouteName(LE_CODES.ElectronicForm[form], "Edit"),
    base: getElectronicFormRouteName(LE_CODES.ElectronicForm[form], "Base"),
    audit: getElectronicFormRouteName(LE_CODES.ElectronicForm[form], "Audit"),
    initiateSubmission: getElectronicFormRouteName(LE_CODES.ElectronicForm[form], "InitiateSubmission"),
    submissionAgreementForPdf: getElectronicFormRouteName(LE_CODES.ElectronicForm[form], "SubmissionAgreementForPdf"),
    reviewSubmission: getElectronicFormRouteName(LE_CODES.ElectronicForm[form], "ReviewSubmission"),
    repudiateSubmission: getElectronicFormRouteName(LE_CODES.ElectronicForm[form], "RepudiateSubmission"),
    worksheets: getElectronicFormRouteName(LE_CODES.ElectronicForm[form], "Worksheets"),
    documents: getElectronicFormRouteName(LE_CODES.ElectronicForm[form], "Documents"),
  };
  return acc;
};

// Dynamically generate all common names
const generatedNames = allDustControlPermittingRoutableForms.reduce(
  nameGenerator<GeneratedDustControlPermittingNames>,
  {},
) as GeneratedDustControlPermittingNames;

// Explicitly defined names go here
type StaticDustControlPermittingNames = {
  dustControlPermitting: string;
  dustControlPermittingList: string;
  dustControlPermittingSelect: string;
};

// Infer the generated names type
type GeneratedDustControlPermittingNames = {
  [K in (typeof allDustControlPermittingRoutableForms)[number]]: {
    new: string;
    view: string;
    edit: string;
    base: string;
    audit: string;
    initiateSubmission: string;
    submissionAgreementForPdf: string;
    reviewSubmission: string;
    repudiateSubmission: string;
    worksheets: string;
    documents: string;
  };
};

// Combined type for all route names -
type DustControlPermitRouteNames = StaticDustControlPermittingNames & GeneratedDustControlPermittingNames;

const names: DustControlPermitRouteNames = {
  dustControlPermitting: "ElectronicForm.DustControlPermitting",
  dustControlPermittingList: "ElectronicForm.DustControlPermittingList",
  dustControlPermittingSelect: "ElectronicForm.DustControlPermittingSelect",
  // dustFormDynamicView: "ElectronicForm.DustFormDynamicView",
  ...generatedNames,
};

const additionalChildRoutes: { [key: string]: RouterOptions["routes"] } = {
  // routes beyond the standard predefined routes should be added here.
  // the key should be the form code, and the value should be the routes.
};

const createRoutes = () => {
  const routes = allDustControlPermittingRoutableForms.map((form) => {
    const formRouteNames = names[form];
    return {
      path: LE_CODES.ElectronicForm[form],
      name: LE_CODES.ElectronicForm[form],
      component: {
        render: () => <RouterView />,
      },
      children: [
        {
          path: "new",
          component: () =>
            import(`@/views/electronicForm/dustControlPermitting/${LE_CODES.ElectronicForm[form]}/Base.vue`),
          redirect: { name: formRouteNames.new },
          params: {
            type: LE_CODES.ElectronicForm[form],
          },
          // hard code the paths based on what form you're making...
          children: [
            {
              path: ":typeMurn?",
              name: formRouteNames.new,
              meta: { title: "New Form", isNew: true },
              component: () =>
                import(`@/views/electronicForm/dustControlPermitting/${LE_CODES.ElectronicForm[form]}/Edit.vue`),
            },
          ],
        },
        {
          path: ":id(\\d+)",
          name: formRouteNames.base,
          component: () =>
            import(`@/views/electronicForm/dustControlPermitting/${LE_CODES.ElectronicForm[form]}/Base.vue`),
          redirect: { name: formRouteNames.view },
          children: [
            {
              path: "view",
              name: formRouteNames.view,
              meta: { title: "Form View" },
              component: () =>
                import(`@/views/electronicForm/dustControlPermitting/${LE_CODES.ElectronicForm[form]}/View.vue`),
            },
            {
              path: "edit",
              name: formRouteNames.edit,
              meta: { title: "Form Edit", isForm: true },
              component: () =>
                import(`@/views/electronicForm/dustControlPermitting/${LE_CODES.ElectronicForm[form]}/Edit.vue`),
            },
            {
              path: "activity",
              name: formRouteNames.audit,
              meta: { title: "Form Audit Activity" },
              component: {
                render: () => <EntityAudit entityType="ElectronicForm" />,
              },
            },
            {
              path: "initiateSubmission",
              name: formRouteNames.initiateSubmission,
              meta: { title: "Submission Agreement", isForm: true },
              ...withProps(() => import("@/components/submission/InitiateSubmission.vue"), {
                formCode: form,
                formRouteNames,
              }),
            },
            {
              // Mirrors the initiateSubmission route for PDF generation.
              path: "submissionAgreementForPdf",
              name: formRouteNames.submissionAgreementForPdf,
              meta: { title: "Submission Agreement", isForm: true },
              ...withProps(() => import("@/components/submission/InitiateSubmission.vue"), {
                formCode: form,
                formRouteNames,
                isSubmissionAgreementForPdf: true,
              }),
            },
            {
              path: "reviewSubmission",
              name: formRouteNames.reviewSubmission,
              meta: { title: "Submission Review", isForm: true },
              ...withProps(() => import("@/components/submission/ReviewSubmission.vue"), {
                formCode: form,
                formRouteNames,
              }),
            },
            {
              path: "repudiateSubmission",
              name: formRouteNames.repudiateSubmission,
              meta: { title: "Submission Repudiation", isForm: true },
              ...withProps(() => import("@/components/submission/RepudiateSubmission.vue"), {
                formCode: form,
                formRouteNames,
              }),
            },
            ...createWorksheetChildRoutes(formRouteNames),
            ...createDocumentChildRoutes(formRouteNames),
            ...(additionalChildRoutes[form] ? additionalChildRoutes[form] : []),
          ],
        },
      ],
    };
  });
  return routes;
};

export { names as dustFormPermittingRouteNames };

const dustFormRoutes: RouterOptions["routes"] = [
  {
    path: "/dustControlPermitting",
    name: names.dustControlPermitting,
    component: {
      render: () => <RouterView />,
    },
    redirect: { name: names.dustControlPermittingList },
    children: [
      {
        path: "list",
        name: names.dustControlPermittingList,
        meta: { title: "Forms", lookupGroups: ["ElectronicForm", "ElectronicFormStatus"] },
        component: () => import("@/views/electronicForm/dustControlPermitting/DustFormList.vue"),
      },
      // Keep this dynamic handler, and change the "name" to be generated based on the type and to go to the view.
      // {
      //   path: "v/:type/:id",
      //   name: names.dustFormDynamicView,
      //   redirect: (to) => {
      //     const type = Array.isArray(to.params.type) ? to.params.type[0] : to.params.type;
      //     const id = Array.isArray(to.params.id) ? to.params.id[0] : to.params.id;
      //     return { name: type, params: { id } };
      //   },
      // },
      {
        path: "formSelect",
        name: names.dustControlPermittingSelect,
        meta: { title: "Select Dust Control Form", isNew: true },
        component: () => import("@/views/electronicForm/dustControlPermitting/DustFormSelect.vue"),
      },
      ...createRoutes(),
      // {
      //   path: LE_CODES.ElectronicForm.ef070,
      //   name: LE_CODES.ElectronicForm.ef070,
      //   component: {
      //     render: () => <RouterView />,
      //   },
      //   children: [
      //     {
      //       path: "new",
      //       component: () =>
      //         import("@/views/electronicForm/dustControlPermitting/070_DustControlOperatingPermit/Base.vue"),
      //       redirect: { name: names.ef070New },
      //       // hard code the paths based on what form you're making...
      //       children: [
      //         {
      //           path: "",
      //           name: names.ef070New,
      //           meta: { title: "New Form", isNew: true },
      //           component: () =>
      //             import("@/views/electronicForm/dustControlPermitting/070_DustControlOperatingPermit/Edit.vue"),
      //         },
      //       ],
      //     },
      //     {
      //       path: ":id(\\d+)",
      //       name: names.ef070Base,
      //       component: () =>
      //         import("@/views/electronicForm/dustControlPermitting/070_DustControlOperatingPermit/Base.vue"),
      //       redirect: { name: names.ef070View },
      //       children: [
      //         {
      //           path: "view",
      //           name: names.ef070View,
      //           meta: { title: "Form View" },
      //           component: () =>
      //             import("@/views/electronicForm/dustControlPermitting/070_DustControlOperatingPermit/View.vue"),
      //         },
      //         {
      //           path: "edit",
      //           name: names.ef070Edit,
      //           meta: { title: "Form Edit", isForm: true },
      //           component: () =>
      //             import("@/views/electronicForm/dustControlPermitting/070_DustControlOperatingPermit/Edit.vue"),
      //         },

      //         {
      //           path: "activity",
      //           name: names.ef070Audit,
      //           meta: { title: "Form Audit Activity" },
      //           component: {
      //             render: () => <EntityAudit entityType="ElectronicForm" />,
      //           },
      //         },
      //       ],
      //     },
      //   ],
      // },
      // {
      //   path: LE_CODES.ElectronicForm.ef071,
      //   name: LE_CODES.ElectronicForm.ef071,
      //   component: {
      //     render: () => <RouterView />,
      //   },
      //   children: [
      //     {
      //       path: "new",
      //       component: () =>
      //         import("@/views/electronicForm/dustControlPermitting/071_ImplosionDustControlOperatingPermit/Base.vue"),
      //       redirect: { name: names.ef071New },
      //       // hard code the paths based on what form you're making...
      //       children: [
      //         {
      //           path: "",
      //           name: names.ef071New,
      //           meta: { title: "New Form", isNew: true },
      //           component: () =>
      //             import(
      //               "@/views/electronicForm/dustControlPermitting/071_ImplosionDustControlOperatingPermit/Edit.vue"
      //             ),
      //         },
      //       ],
      //     },
      //     {
      //       path: ":id(\\d+)",
      //       name: names.ef071Base,
      //       component: () =>
      //         import("@/views/electronicForm/dustControlPermitting/071_ImplosionDustControlOperatingPermit/Base.vue"),
      //       redirect: { name: names.ef071View },
      //       children: [
      //         {
      //           path: "view",
      //           name: names.ef071View,
      //           meta: { title: "Form View" },
      //           component: () =>
      //             import(
      //               "@/views/electronicForm/dustControlPermitting/071_ImplosionDustControlOperatingPermit/View.vue"
      //             ),
      //         },
      //         {
      //           path: "edit",
      //           name: names.ef071Edit,
      //           meta: { title: "Form Edit", isForm: true },
      //           component: () =>
      //             import(
      //               "@/views/electronicForm/dustControlPermitting/071_ImplosionDustControlOperatingPermit/Edit.vue"
      //             ),
      //         },

      //         {
      //           path: "activity",
      //           name: names.ef071Audit,
      //           meta: { title: "Form Audit Activity" },
      //           component: {
      //             render: () => <EntityAudit entityType="ElectronicForm" />,
      //           },
      //         },
      //       ],
      //     },
      //   ],
      // },
    ],
  },
];
export default dustFormRoutes;
